import {
  Loader2,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { UnifiedCommunication } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

// Format duration for display
const formatDuration = (seconds?: number) => {
  if (!seconds) return null;

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} hr ${minutes} min`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return 'Less than 1 min';
};

// Format date/time for display
const formatDateTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const isThisYear = date.getFullYear() === now.getFullYear();

  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: isThisYear ? undefined : 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateStr} \u2022 ${timeStr}`;
};

interface CallCardProps {
  call: UnifiedCommunication;
}

const CallCard: React.FC<CallCardProps> = ({ call }) => {
  const { selectCommunication, isRead } = useClientCommunications();

  const isInbound = call.direction === 'inbound';
  const isMissed = !call.duration_seconds || call.duration_seconds < 10;
  const read = isRead(call.id);

  const handleSelect = () => {
    selectCommunication(call);
  };

  // Determine icon and colors based on call type
  const getCallIcon = () => {
    if (isMissed && isInbound) {
      return {
        Icon: PhoneMissed,
        bgColor: 'bg-red-50',
        iconColor: 'text-red-500',
      };
    }
    if (isInbound) {
      return {
        Icon: PhoneIncoming,
        bgColor: 'bg-primary/10',
        iconColor: 'text-primary',
      };
    }
    return {
      Icon: PhoneOutgoing,
      bgColor: 'bg-primary/10',
      iconColor: 'text-primary',
    };
  };

  const { Icon, bgColor, iconColor } = getCallIcon();

  // Generate a display title from preview/content
  const getCallTitle = () => {
    // If there's a preview/summary from AI, use the first sentence or phrase
    if (call.preview && call.preview.length > 10) {
      // Take first sentence or first 60 chars
      const firstSentence = call.preview.split(/[.!?]/)[0];
      if (firstSentence.length > 60) {
        return `${firstSentence.substring(0, 57)}...`;
      }
      return firstSentence;
    }

    // Default titles
    if (isMissed && isInbound) {
      return 'Attempted call to client';
    }
    if (isMissed && !isInbound) {
      return 'Attempted call to client';
    }
    if (isInbound) {
      return 'Incoming call from client';
    }
    return 'Outgoing call to client';
  };

  const durationLabel = isMissed
    ? isInbound
      ? 'Missed'
      : 'No answer'
    : formatDuration(call.duration_seconds);

  // Get summary text (preview or content)
  const getSummary = () => {
    if (isMissed) {
      return isInbound ? 'Missed call from client.' : 'Client unavailable. Voicemail message left.';
    }
    return call.content || call.preview || '';
  };

  return (
    <div
      className={cn(
        'border-b border-zinc-100 last:border-b-0',
        !read && isInbound && 'bg-primary/[0.02]',
      )}
    >
      <div
        className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-50/80 transition-colors cursor-pointer"
        onClick={handleSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleSelect();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center',
            bgColor,
          )}
        >
          <Icon className={cn('h-4 w-4', iconColor)} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row with duration */}
          <div className="flex items-center gap-2 mb-0.5">
            <h4
              className={cn(
                'text-sm font-bold text-zinc-900',
                !read && isInbound && 'text-zinc-950',
              )}
            >
              {getCallTitle()}
            </h4>
            {durationLabel && (
              <span className="text-xs font-semibold text-zinc-600">
                • {durationLabel}
              </span>
            )}
          </div>

          {/* Summary */}
          <p className="text-sm font-medium text-zinc-700 leading-relaxed line-clamp-2">
            {getSummary()}
          </p>
        </div>

        {/* Date/time - right aligned */}
        <div className="flex-shrink-0 text-right">
          <span className="text-xs font-semibold text-zinc-500">
            {formatDateTime(call.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const CallsView: React.FC = () => {
  const { communications, isLoading } = useClientCommunications();

  // Filter to only phone calls
  const phoneCalls = communications.filter((c) => c.type === 'phone_call');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (phoneCalls.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center px-4">
        <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
          <Phone className="h-6 w-6 text-zinc-400" />
        </div>
        <p className="text-base font-semibold text-zinc-800 mb-1">
          No call history
        </p>
        <p className="text-sm text-zinc-500">
          Calls with this client will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {phoneCalls.map((call) => (
        <CallCard key={call.id} call={call} />
      ))}
    </div>
  );
};

export default CallsView;
